import { notification } from "antd";
import React, { useCallback, useEffect, useState } from "react";

import {
  NEW_OPERATOR_SENTINEL,
  Operator,
  OperatorCreatePayload,
  OperatorUpdatePayload,
} from "../../../types";
import {
  createOperator,
  deleteOperator as deleteOperatorApi,
  listOperators,
  updateOperator,
} from "../../../../modules/integrations/accounts";
import { getErrorMessage } from "../../../utils/error";
import { ContentWrapper } from "../../common/styledComponents";
import PermissionsTable from "./PermissionsTable";
import { useAuth } from "@/modules/auth";

const TaxOpsView = () => {
  const { getOperatorFirm } = useAuth();
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState<Array<Operator>>([]);

  const deleteOperator = useCallback(
    async (operator: Operator) => {
      const previous = operators;
      try {
        setLoading(true);
        setOperators(operators.filter((item) => item.id !== operator.id));
        await deleteOperatorApi(operator.id);
        notification.success({ message: "Operator deleted" });
      } catch (err) {
        // The optimistic removal didn't stick — restore the row.
        setOperators(previous);
        notification.error({ message: getErrorMessage(err) });
      } finally {
        setLoading(false);
      }
    },
    [operators]
  );

  const fetchOperators = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listOperators();
      setOperators(data);
    } catch (err) {
      notification.error({ message: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  const saveOperator = useCallback(
    async (draft: Operator) => {
      try {
        setLoading(true);
        if (draft.id === NEW_OPERATOR_SENTINEL) {
          const payload: OperatorCreatePayload = {
            email: draft.email,
            displayName: draft.displayName,
            permissionScope: draft.permissionScope,
            firm: draft.firm,
          };
          const created = await createOperator(payload);
          setOperators([
            created,
            ...operators.filter((o) => o.id !== draft.id),
          ]);
        } else {
          const payload: OperatorUpdatePayload = {
            displayName: draft.displayName,
            permissionScope: draft.permissionScope,
            firm: draft.firm,
          };
          const updated = await updateOperator(draft.id, payload);
          setOperators(operators.map((o) => (o.id === draft.id ? updated : o)));
        }
      } catch (err) {
        notification.error({ message: getErrorMessage(err) });
        // Re-throw so the editable table keeps the row open for a retry
        // instead of committing an unsaved draft.
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [operators]
  );

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  return (
    <ContentWrapper>
      <PermissionsTable
        defaultFirm={getOperatorFirm()}
        operators={operators}
        loading={loading}
        onDelete={deleteOperator}
        onSave={saveOperator}
      />
    </ContentWrapper>
  );
};

export default TaxOpsView;
